<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { getContrastingTextColor } from '@chatwoot/utils';
import { resolveColor } from 'shared/helpers/colorHelper';
import Widget from 'dashboard/modules/widget-preview/components/Widget.vue';
import DialoguePreview from 'dashboard/modules/widget-preview/components/DialoguePreview.vue';
import InputRadioGroup from './components/InputRadioGroup.vue';
import ConversationStarters from './components/ConversationStarters.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import IconUploader from './components/IconUploader.vue';
import Input from 'v3/components/Form/Input.vue';
import ColorInput from 'dashboard/components-next/colorpicker/ColorInput.vue';
import Tabs from 'dashboard/components/ui/Tabs/Tabs.vue';
import TabsItem from 'dashboard/components/ui/Tabs/TabsItem.vue';
import GalleryImagePicker from './components/GalleryImagePicker.vue';
import WidgetExperiencesChecklist from './components/WidgetExperiencesChecklist.vue';
import { URLPattern } from 'urlpattern-polyfill';

export default {
  components: {
    ConversationStarters,
    Widget,
    DialoguePreview,
    InputRadioGroup,
    NextButton,
    Editor,
    Avatar,
    IconUploader,
    Input,
    ColorInput,
    Tabs,
    TabsItem,
    GalleryImagePicker,
    WidgetExperiencesChecklist,
  },
  props: {
    inbox: {
      type: Object,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      activeTab: 'general',
      isWidgetPreview: true,
      color: '#7663AB',
      backgroundColor: '#292932',
      websiteName: '',
      welcomeHeading: '',
      welcomeTagline: '',
      welcomeDisclaimer: '',
      replyTime: 'in_a_few_minutes',
      avatarFile: null,
      avatarUrl: '',
      widgetBubblePosition: 'right',
      widgetBubbleLauncherTitle: this.$t(
        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.DEFAULT'
      ),
      widgetBubbleType: 'standard',
      customBubbleIconFile: null,
      customBubbleIconUrl: '',
      deleteCustomBubbleIcon: false,
      customBubbleWidth: 64,
      customBubbleHeight: 64,
      bubbleIconName: '',
      iconMode: 'none',
      preferBuilderSettings: false,
      watchUrlChanges: false,
      allowedUrlPatterns: [],
      allowedUrlPatternErrors: [],
      disallowedUrlPatterns: [],
      disallowedUrlPatternErrors: [],
      showBubbleAdvanced: false,
      homeViewEnabled: false,
      showHeader: true,
      isDialoguePreview: true,
      dialoguePreviewView: 'home',
      widgetBubblePositions: [
        {
          id: 'left',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.LEFT'
          ),
          checked: false,
        },
        {
          id: 'right',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.RIGHT'
          ),
          checked: true,
        },
        {
          id: 'center',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.CENTER'
          ),
          checked: false,
        },
      ],
      widgetBubbleTypes: [
        {
          id: 'standard',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.STANDARD'
          ),
          checked: true,
        },
        {
          id: 'expanded_bubble',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.EXPANDED_BUBBLE'
          ),
          checked: false,
        },
        {
          id: 'label',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.LABEL'
          ),
          checked: false,
        },
        {
          id: 'flap',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.FLAP'
          ),
          checked: false,
        },
        {
          id: 'input_bar',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.INPUT_BAR'
          ),
          checked: false,
        },
        {
          id: 'bar',
          title: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.BAR'
          ),
          checked: false,
        },
      ],
      bubbleIcons: [
        {
          value: 'arrow-right',
          label: 'Arrow Right',
          icon: 'i-lucide-arrow-right',
        },
        {
          value: 'message-circle',
          label: 'Message',
          icon: 'i-lucide-message-circle',
        },
        { value: 'send', label: 'Send', icon: 'i-lucide-send' },
        { value: 'sparkles', label: 'Sparkles', icon: 'i-lucide-sparkles' },
        { value: 'zap', label: 'Zap', icon: 'i-lucide-zap' },
        { value: 'search', label: 'Search', icon: 'i-lucide-search' },
        { value: 'plus', label: 'Plus', icon: 'i-lucide-plus' },
        {
          value: 'help-circle',
          label: 'Help',
          icon: 'i-lucide-message-circle-question-mark',
        },
        {
          value: 'headphones',
          label: 'Headphones',
          icon: 'i-lucide-headphones',
        },
        { value: 'phone', label: 'Phone', icon: 'i-lucide-phone' },
        { value: 'calendar', label: 'Calendar', icon: 'i-lucide-calendar' },
        { value: 'map-pin', label: 'Location', icon: 'i-lucide-map-pin' },
      ],
      conversationStarters: [],
      experiencesEnabled: false,
      selectedWidgetExperienceIds: [],
      newStarter: {
        view: 'home',
        text: '',
        url: '',
        preview: false,
      },
      editingStarterIndex: -1,
      widgetTheme: {
        primaryColor: '#7663ab',
        backgroundColor: '#ffffff',
        bannerBgColor: '#7663ab',
        bannerTextColor: '#ffffff',
        bannerIconColor: '#ffffff',
        userBubbleBgColor: '#7663ab',
        userBubbleTextColor: '#ffffff',
        agentBubbleBgColor: '#f4f6fb',
        agentBubbleTextColor: '#3c4858',
        statusBubbleBgColor: '#f4f6fb',
        statusBubbleTextColor: '#3c4858',
        mutedTextColor: '#3c4858',
        inputBgColor: '#ffffff',
        inputTextColor: '#3c4858',
        inputPlaceholderColor: '#9ca3af',
        formFieldBgColor: 'transparent',
        borderColor: '#e0e6ed',
        borderRadius: 16,
        boxShadow: '0 8px 40px rgba(0, 0, 0, 0.16)',
        fontFamily: '',
        backgroundImageUrl: '',
      },
      dialogueTheme: {
        primaryColor: '#7663ab',
        backgroundColor: '#292932',
        bannerBgColor: '#1d1d22',
        bannerTextColor: '#ffffff',
        bannerIconColor: '#ffffff',
        userBubbleBgColor: '#7663ab',
        userBubbleTextColor: '#ffffff',
        agentBubbleTextColor: '#ffffff',
        statusBubbleBgColor: '#1d1d22',
        statusBubbleTextColor: '#ffffff',
        mutedTextColor: '#ffffff',
        inputBgColor: '#ffffff',
        inputTextColor: '#3c4858',
        inputPlaceholderColor: '#9ca3af',
        formFieldBgColor: 'transparent',
        borderColor: '#e0e6ed',
        borderRadius: 16,
        boxShadow: '0 8px 40px rgba(0, 0, 0, 0.16)',
        fontFamily: '',
        backgroundImageUrl: '',
      },
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
      planningExperiences: 'planningExperiences/getRecords',
      planningExperiencesUiFlags: 'planningExperiences/getUIFlags',
    }),
    activePlanningExperiences() {
      return (this.planningExperiences || []).filter(
        experience => experience.active
      );
    },
    storageKey() {
      return `${LOCAL_STORAGE_KEYS.WIDGET_BUILDER}${this.inbox.id}`;
    },
    widgetScript() {
      return this.inbox.web_widget_script;
    },
    dialogueScript() {
      const baseUrl = window.location.origin;
      const token = this.inbox.website_token || 'YOUR_WEBSITE_TOKEN';
      return `(function (d, t) {
  var BASE_URL="${baseUrl}";
  var g = d.createElement(t),
    s = d.getElementsByTagName(t)[0];
  g.src = BASE_URL + "/packs/js/dialoguesdk.js";
  g.defer = true;
  g.async = true;
  s.parentNode.insertBefore(g, s);

  g.onload = function () {
    window.dialogueSDK.run({
      container: document.getElementById("your-container-id"),
      websiteToken: "${token}",
      baseUrl: BASE_URL,
    });
  };
})(document, "script");`;
    },
    getReplyTimeOptions() {
      return [
        {
          key: 'disabled',
          value: 'disabled',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.DISABLED'
          ),
        },
        {
          key: 'in_a_few_minutes',
          value: 'in_a_few_minutes',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_FEW_MINUTES'
          ),
        },
        {
          key: 'in_a_few_hours',
          value: 'in_a_few_hours',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_FEW_HOURS'
          ),
        },
        {
          key: 'in_a_day',
          value: 'in_a_day',
          text: this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.IN_A_DAY'
          ),
        },
      ];
    },
    websiteNameValidationErrorMsg() {
      return this.v$.websiteName.$error
        ? this.$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.ERROR')
        : '';
    },
    isEditing() {
      return this.editingStarterIndex !== -1;
    },
    previewThemeVars() {
      const t =
        (this.activeTab === 'widget-theme'
          ? this.widgetTheme
          : this.dialogueTheme) || {};
      const primary = resolveColor(t.primaryColor, this.color || '#7663ab');
      const bg = resolveColor(t.backgroundColor, '#ffffff');
      const bannerBg = resolveColor(t.bannerBgColor, primary);
      const bannerText = resolveColor(
        t.bannerTextColor,
        getContrastingTextColor(bannerBg)
      );
      const bannerIconColor = resolveColor(t.bannerIconColor, bannerText);
      const userBubbleBg = resolveColor(t.userBubbleBgColor, primary);
      const userBubbleText = resolveColor(
        t.userBubbleTextColor,
        getContrastingTextColor(userBubbleBg)
      );
      const agentBubbleBg = resolveColor(t.agentBubbleBgColor, '#f4f6fb');
      const agentBubbleText = resolveColor(t.agentBubbleTextColor, '#3c4858');
      const statusBubbleBg = resolveColor(t.statusBubbleBgColor, bannerBg);
      const statusBubbleText = resolveColor(
        t.statusBubbleTextColor,
        bannerText
      );
      const mutedTextColor = resolveColor(t.mutedTextColor, '#3c4858');
      const inputBg = resolveColor(t.inputBgColor, '#ffffff');
      const inputText = resolveColor(t.inputTextColor, '#3c4858');
      const inputPlaceholder = resolveColor(t.inputPlaceholderColor, '#9ca3af');
      const formFieldBg = resolveColor(t.formFieldBgColor, 'transparent');
      const borderColor = resolveColor(t.borderColor, '#e0e6ed');
      const borderRadius = t.borderRadius ? `${t.borderRadius}px` : '16px';
      const boxShadow = resolveColor(
        t.boxShadow,
        '0 8px 40px rgba(0, 0, 0, 0.16)'
      );
      const DEFAULT_FONT =
        "'MonaSans', -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
      const fontFamily = t.fontFamily
        ? `'${t.fontFamily}', ${DEFAULT_FONT}`
        : DEFAULT_FONT;
      return {
        '--wt-primary': primary,
        '--wt-bg': bg,
        '--wt-banner-bg': bannerBg,
        '--wt-banner-text': bannerText,
        '--wt-banner-icon-color': bannerIconColor,
        '--wt-user-bubble-bg': userBubbleBg,
        '--wt-user-bubble-text': userBubbleText,
        '--wt-agent-bubble-bg': agentBubbleBg,
        '--wt-agent-bubble-text': agentBubbleText,
        '--wt-status-bubble-bg': statusBubbleBg,
        '--wt-status-bubble-text': statusBubbleText,
        '--wt-muted-text': mutedTextColor,
        '--wt-input-bg': inputBg,
        '--wt-input-text': inputText,
        '--wt-input-placeholder': inputPlaceholder,
        '--wt-form-field-bg': formFieldBg,
        '--wt-border-color': borderColor,
        '--wt-border-radius': borderRadius,
        '--wt-shadow': boxShadow,
        '--wt-gradient':
          boxShadow === 'none'
            ? 'none'
            : 'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.4))',
        '--wt-font': fontFamily,
        '--wt-bg-image': t.backgroundImageUrl
          ? `url(${JSON.stringify(t.backgroundImageUrl)})`
          : 'none',
        fontFamily,
      };
    },
    canAddStarter() {
      return (
        (!this.newStarter.url && this.newStarter.text) ||
        (this.newStarter.url && this.newStarter.view)
      );
    },
  },
  watch: {
    inbox: {
      handler() {
        this.setDefaults();
        this.initWidgetExperienceSelection();
      },
      deep: true,
    },
    experiencesEnabled(enabled) {
      if (
        enabled &&
        (this.inbox.widget_planning_experience_ids === null ||
          this.inbox.widget_planning_experience_ids === undefined)
      ) {
        this.initWidgetExperienceSelection();
      }
    },
  },
  mounted() {
    this.setDefaults();
    this.$store.dispatch('planningExperiences/get').then(() => {
      this.initWidgetExperienceSelection();
    });
  },
  validations: {
    websiteName: { required },
  },
  methods: {
    initWidgetExperienceSelection() {
      const stored = this.inbox.widget_planning_experience_ids;
      const activeIds = this.activePlanningExperiences.map(
        experience => experience.id
      );
      if (stored === null || stored === undefined) {
        this.selectedWidgetExperienceIds = [...activeIds];
      } else if (stored.length === 0) {
        this.selectedWidgetExperienceIds = [];
      } else {
        this.selectedWidgetExperienceIds = stored.filter(id =>
          activeIds.includes(id)
        );
      }
    },
    widgetPlanningExperiencePayload() {
      if (!this.experiencesEnabled) {
        return {};
      }
      const activeIds = this.activePlanningExperiences.map(
        experience => experience.id
      );
      const selected = this.selectedWidgetExperienceIds;
      const allSelected =
        activeIds.length > 0 && activeIds.every(id => selected.includes(id));

      if (allSelected) {
        return { widget_planning_experience_ids_all: true };
      }
      if (selected.length === 0) {
        return { widget_planning_experience_ids: [] };
      }
      return { widget_planning_experience_ids: selected };
    },
    setDefaults() {
      // Widget Settings
      const {
        name,
        welcome_title,
        welcome_tagline,
        welcome_disclaimer,
        widget_color,
        widget_backgroundcolor,
        widget_theme,
        dialogue_theme,
        reply_time,
        avatar_url,
        conversation_starters,
        channel_options,
      } = this.inbox;
      this.websiteName = name || '';
      this.welcomeHeading = welcome_title || '';
      this.welcomeTagline = welcome_tagline || '';
      this.welcomeDisclaimer = welcome_disclaimer || '';
      this.color = widget_color;
      this.backgroundColor = widget_backgroundcolor;
      this.replyTime = reply_time;
      this.avatarUrl = avatar_url;
      this.conversationStarters = conversation_starters;
      this.customBubbleIconUrl = channel_options?.customBubbleIcon || '';
      this.customBubbleWidth = channel_options?.customBubbleWidth || 64;
      this.customBubbleHeight = channel_options?.customBubbleHeight || 64;
      this.bubbleIconName = channel_options?.bubbleIconName || '';
      if (channel_options?.customBubbleIcon) {
        this.iconMode = 'custom';
      } else if (channel_options?.bubbleIconName) {
        this.iconMode = 'predefined';
      } else {
        this.iconMode = 'none';
      }
      this.homeViewEnabled = !!channel_options?.homeViewEnabled;
      this.showHeader = channel_options?.showHeader !== false;
      this.experiencesEnabled = !!channel_options?.experiencesEnabled;
      this.preferBuilderSettings = !!channel_options?.preferBuilderSettings;
      this.watchUrlChanges = !!channel_options?.watchUrlChanges;
      this.allowedUrlPatterns = Array.isArray(
        channel_options?.allowedUrlPatterns
      )
        ? channel_options.allowedUrlPatterns.filter(Boolean)
        : [];
      this.allowedUrlPatternErrors = this.allowedUrlPatterns.map(() => '');
      this.disallowedUrlPatterns = Array.isArray(
        channel_options?.disallowedUrlPatterns
      )
        ? channel_options.disallowedUrlPatterns.filter(Boolean)
        : [];
      this.disallowedUrlPatternErrors = this.disallowedUrlPatterns.map(
        () => ''
      );
      this.initWidgetExperienceSelection();
      if (widget_theme && Object.keys(widget_theme).length) {
        this.widgetTheme = { ...this.widgetTheme, ...widget_theme };
      } else if (widget_color || widget_backgroundcolor) {
        // Migrate legacy colors into the widget theme — pre-fill with what was configured
        if (widget_color) {
          this.widgetTheme.primaryColor = widget_color;
          this.widgetTheme.bannerBgColor = widget_color;
          this.widgetTheme.userBubbleBgColor = widget_color;
        }
        if (widget_backgroundcolor) {
          this.widgetTheme.backgroundColor = widget_backgroundcolor;
        }
      }
      if (dialogue_theme && Object.keys(dialogue_theme).length) {
        this.dialogueTheme = { ...this.dialogueTheme, ...dialogue_theme };
      } else if (widget_color) {
        this.dialogueTheme.primaryColor = widget_color;
        this.dialogueTheme.userBubbleBgColor = widget_color;
      }

      const savedInformation = this.getSavedInboxInformation();
      this.applyBubbleLauncherSettings({
        position: channel_options?.bubblePosition || savedInformation?.position,
        type: channel_options?.bubbleType || savedInformation?.type,
        launcherTitle:
          channel_options?.launcherTitle || savedInformation?.launcherTitle,
      });
    },
    applyBubbleLauncherSettings({ position, type, launcherTitle }) {
      if (position) {
        this.widgetBubblePositions = this.widgetBubblePositions.map(item => {
          const checked = item.id === position;
          if (checked) {
            this.widgetBubblePosition = item.id;
          }
          return { ...item, checked };
        });
      }
      if (type) {
        this.widgetBubbleTypes = this.widgetBubbleTypes.map(item => {
          const checked = item.id === type;
          if (checked) {
            this.widgetBubbleType = item.id;
          }
          return { ...item, checked };
        });
      }
      if (launcherTitle) {
        this.widgetBubbleLauncherTitle = launcherTitle;
      }
    },
    handleWidgetBubblePositionChange(item) {
      this.widgetBubblePosition = item.id;
    },
    handleWidgetBubbleTypeChange(item) {
      this.widgetBubbleType = item.id;
      // Input bar defaults to center position
      if (item.id === 'input_bar') {
        this.widgetBubblePosition = 'center';
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        await this.$store.dispatch('inboxes/deleteInboxAvatar', this.inbox.id);
        this.avatarFile = null;
        this.avatarUrl = '';
        useAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.DELETE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.DELETE.API.ERROR_MESSAGE'
              )
        );
      }
    },
    handleCustomBubbleIconUpload({ file, url }) {
      this.customBubbleIconFile = file;
      this.customBubbleIconUrl = url;
    },
    handleCustomBubbleIconDelete() {
      this.customBubbleIconFile = null;
      this.customBubbleIconUrl = '';
      this.deleteCustomBubbleIcon = true;

      // If the icon is already saved on the server, delete it
      if (this.inbox?.channel?.channel_options?.customBubbleIcon) {
        this.$store.dispatch('inboxes/deleteCustomBubbleIcon', this.inbox.id);
      }
    },
    setIconMode(mode) {
      if (mode !== 'custom') {
        this.customBubbleIconFile = null;
        this.customBubbleIconUrl = '';
        this.deleteCustomBubbleIcon = true;
      }
      if (mode !== 'predefined') {
        this.bubbleIconName = '';
      }
      this.iconMode = mode;
    },
    addConversationStarter() {
      if (!this.canAddStarter) return;

      if (!Array.isArray(this.conversationStarters)) {
        this.conversationStarters = [];
      }

      const starter = {
        view: this.newStarter.view,
        text: this.newStarter.text,
        url: this.newStarter.url || null,
        preview: this.newStarter.preview,
      };

      if (this.isEditing) {
        this.conversationStarters.splice(this.editingStarterIndex, 1, starter);
        this.editingStarterIndex = -1;
      } else {
        this.conversationStarters.push(starter);
      }

      this.resetNewStarter();
    },
    editConversationStarter(index) {
      const starter = this.conversationStarters[index];
      this.newStarter = {
        view: starter.view,
        text: starter.text || '',
        url: starter.url || '',
        preview: starter.preview || false,
      };
      this.editingStarterIndex = index;
    },
    deleteConversationStarter(index) {
      this.conversationStarters.splice(index, 1);
      if (this.editingStarterIndex === index) {
        this.resetNewStarter();
      }
    },
    resetNewStarter() {
      this.newStarter = {
        view: 'home',
        text: '',
        url: '',
        preview: false,
      };
      this.editingStarterIndex = -1;
    },
    isValidUrlPattern(value) {
      if (!value) return true;
      if (!value.startsWith('http://') && !value.startsWith('https://')) {
        return false;
      }
      try {
        // eslint-disable-next-line no-new
        new URLPattern(value);
        return true;
      } catch {
        return false;
      }
    },
    validateAllowedUrlPatterns() {
      this.allowedUrlPatternErrors = this.allowedUrlPatterns.map(pattern => {
        const trimmed = (pattern || '').trim();
        if (!trimmed) return '';
        return this.isValidUrlPattern(trimmed)
          ? ''
          : this.$t(
              'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.ALLOWED_URL_PATTERNS.ERROR'
            );
      });
      return this.allowedUrlPatternErrors.every(error => !error);
    },
    validateDisallowedUrlPatterns() {
      this.disallowedUrlPatternErrors = this.disallowedUrlPatterns.map(
        pattern => {
          const trimmed = (pattern || '').trim();
          if (!trimmed) return '';
          return this.isValidUrlPattern(trimmed)
            ? ''
            : this.$t(
                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISALLOWED_URL_PATTERNS.ERROR'
              );
        }
      );
      return this.disallowedUrlPatternErrors.every(error => !error);
    },
    addAllowedUrlPattern() {
      this.allowedUrlPatterns.push('');
      this.allowedUrlPatternErrors.push('');
    },
    removeAllowedUrlPattern(index) {
      this.allowedUrlPatterns.splice(index, 1);
      this.allowedUrlPatternErrors.splice(index, 1);
    },
    addDisallowedUrlPattern() {
      this.disallowedUrlPatterns.push('');
      this.disallowedUrlPatternErrors.push('');
    },
    removeDisallowedUrlPattern(index) {
      this.disallowedUrlPatterns.splice(index, 1);
      this.disallowedUrlPatternErrors.splice(index, 1);
    },
    async updateWidget() {
      if (
        !this.validateAllowedUrlPatterns() ||
        !this.validateDisallowedUrlPatterns()
      ) {
        return;
      }
      try {
        const payload = {
          id: this.inbox.id,
          name: this.websiteName,
          ...this.widgetPlanningExperiencePayload(),
          channel: {
            widget_color: this.widgetTheme.primaryColor || this.color,
            widget_backgroundcolor:
              this.dialogueTheme.backgroundColor || this.backgroundColor,
            welcome_title: this.welcomeHeading,
            welcome_tagline: this.welcomeTagline,
            welcome_disclaimer: this.welcomeDisclaimer,
            reply_time: this.replyTime,
            conversation_starters: this.conversationStarters,
            widget_theme: this.widgetTheme,
            dialogue_theme: this.dialogueTheme,
            channel_options: {
              ...(this.inbox.channel_options || {}),
              customBubbleWidth: this.customBubbleWidth,
              customBubbleHeight: this.customBubbleHeight,
              bubbleIconName: this.bubbleIconName,
              homeViewEnabled: this.homeViewEnabled,
              showHeader: this.showHeader,
              experiencesEnabled: this.experiencesEnabled,
              bubblePosition: this.widgetBubblePosition,
              bubbleType: this.widgetBubbleType,
              launcherTitle: this.widgetBubbleLauncherTitle,
              preferBuilderSettings: this.preferBuilderSettings,
              watchUrlChanges: this.watchUrlChanges,
              allowedUrlPatterns: this.allowedUrlPatterns
                .map(pattern => pattern.trim())
                .filter(Boolean),
              disallowedUrlPatterns: this.disallowedUrlPatterns
                .map(pattern => pattern.trim())
                .filter(Boolean),
            },
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }
        if (this.customBubbleIconFile) {
          payload.channel.custom_bubble_icon = this.customBubbleIconFile;
        } else if (this.deleteCustomBubbleIcon) {
          // Explicitly remove the icon from channel_options when deleted
          delete payload.channel.channel_options.customBubbleIcon;
        }

        await this.$store.dispatch('inboxes/updateInbox', payload);

        // Reset the delete flag after successful update
        this.deleteCustomBubbleIcon = false;

        useAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          error.message ||
            this.$t(
              'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.API.ERROR_MESSAGE'
            )
        );
      }
    },
    getSavedInboxInformation() {
      return LocalStorage.get(this.storageKey);
    },
    resetWidgetTheme() {
      this.widgetTheme = {
        primaryColor: '#7663ab',
        backgroundColor: '#ffffff',
        bannerBgColor: '#7663ab',
        bannerTextColor: '#ffffff',
        bannerIconColor: '#ffffff',
        userBubbleBgColor: '#7663ab',
        userBubbleTextColor: '#ffffff',
        agentBubbleBgColor: '#f4f6fb',
        agentBubbleTextColor: '#3c4858',
        statusBubbleBgColor: '#f4f6fb',
        statusBubbleTextColor: '#3c4858',
        mutedTextColor: '#3c4858',
        inputBgColor: '#ffffff',
        inputTextColor: '#3c4858',
        inputPlaceholderColor: '#9ca3af',
        formFieldBgColor: 'transparent',
        borderColor: '#e0e6ed',
        borderRadius: 16,
        boxShadow: '0 8px 40px rgba(0, 0, 0, 0.16)',
        fontFamily: '',
        backgroundImageUrl: '',
      };
    },
    resetDialogueTheme() {
      this.dialogueTheme = {
        primaryColor: '#7663ab',
        backgroundColor: '#292932',
        bannerBgColor: '#1d1d22',
        bannerTextColor: '#ffffff',
        bannerIconColor: '#ffffff',
        userBubbleBgColor: '#7663ab',
        userBubbleTextColor: '#ffffff',
        agentBubbleTextColor: '#ffffff',
        statusBubbleBgColor: '#1d1d22',
        statusBubbleTextColor: '#ffffff',
        mutedTextColor: '#ffffff',
        inputBgColor: '#ffffff',
        inputTextColor: '#3c4858',
        inputPlaceholderColor: '#9ca3af',
        formFieldBgColor: 'transparent',
        borderColor: '#e0e6ed',
        borderRadius: 16,
        boxShadow: '0 8px 40px rgba(0, 0, 0, 0.16)',
        fontFamily: '',
        backgroundImageUrl: '',
      };
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <div class="flex gap-6">
      <div class="w-full">
        <div class="min-h-full py-4 px-px">
          <!-- Tabs -->
          <div class="flex gap-2 mb-6 border-b border-n-slate-6">
            <button
              type="button"
              class="px-4 py-3 font-medium transition-colors border-b-2"
              :class="
                activeTab === 'general'
                  ? 'border-woot-500 text-woot-500'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
              "
              @click="activeTab = 'general'"
            >
              {{ $t('INBOX_MGMT.WIDGET_BUILDER.TABS.GENERAL') }}
            </button>
            <button
              type="button"
              class="px-4 py-3 font-medium transition-colors border-b-2"
              :class="
                activeTab === 'bubble'
                  ? 'border-woot-500 text-woot-500'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
              "
              @click="activeTab = 'bubble'"
            >
              {{ $t('INBOX_MGMT.WIDGET_BUILDER.TABS.BUBBLE') }}
            </button>
            <button
              type="button"
              class="px-4 py-3 font-medium transition-colors border-b-2"
              :class="
                activeTab === 'advanced'
                  ? 'border-woot-500 text-woot-500'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
              "
              @click="activeTab = 'advanced'"
            >
              {{ $t('INBOX_MGMT.WIDGET_BUILDER.TABS.ADVANCED') }}
            </button>
            <button
              type="button"
              class="px-4 py-3 font-medium transition-colors border-b-2"
              :class="
                activeTab === 'widget-theme'
                  ? 'border-woot-500 text-woot-500'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
              "
              @click="activeTab = 'widget-theme'"
            >
              {{ $t('INBOX_MGMT.WIDGET_BUILDER.THEME.TABS.WIDGET') }}
            </button>
            <button
              type="button"
              class="px-4 py-3 font-medium transition-colors border-b-2"
              :class="
                activeTab === 'dialogue-theme'
                  ? 'border-woot-500 text-woot-500'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
              "
              @click="activeTab = 'dialogue-theme'"
            >
              {{ $t('INBOX_MGMT.WIDGET_BUILDER.THEME.TABS.DIALOGUE') }}
            </button>
          </div>

          <form @submit.prevent="updateWidget">
            <!-- General Tab -->
            <div v-show="activeTab === 'general'" class="space-y-4">
              <div class="flex flex-col mb-4 items-start gap-1 w-full">
                <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                  {{
                    $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.AVATAR.LABEL')
                  }}
                </label>
                <Avatar
                  :src="avatarUrl"
                  :size="72"
                  icon-name="i-ri-global-fill"
                  name=""
                  allow-upload
                  rounded-full
                  @upload="handleImageUpload"
                  @delete="handleAvatarDelete"
                />
              </div>
              <Input
                v-model="websiteName"
                name="websiteName"
                :label="
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.LABEL'
                  )
                "
                :placeholder="
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WEBSITE_NAME.PLACE_HOLDER'
                  )
                "
                :has-error="v$.websiteName.$error"
                :error-message="websiteNameValidationErrorMsg"
                @blur="v$.websiteName.$touch"
              />
              <Input
                v-model="welcomeHeading"
                name="welcomeHeading"
                :label="
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_HEADING.LABEL'
                  )
                "
                :placeholder="
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_HEADING.PLACE_HOLDER'
                  )
                "
              />
              <Editor
                v-model="welcomeTagline"
                :label="
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_TAGLINE.LABEL'
                  )
                "
                :placeholder="
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_TAGLINE.PLACE_HOLDER'
                  )
                "
                :max-length="255"
                channel-type="Context::InboxSettings"
                class="mb-4"
              />
              <label>
                {{
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_DISCLAIMER.LABEL'
                  )
                }}
              </label>
              <textarea
                v-model.trim="welcomeDisclaimer"
                rows="3"
                type="text"
                :placeholder="
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_DISCLAIMER.PLACE_HOLDER'
                  )
                "
              />
              <p class="text-sm">
                {{
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WELCOME_DISCLAIMER.HELP_TEXT'
                  )
                }}
              </p>
              <label>
                {{
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.REPLY_TIME.LABEL'
                  )
                }}
                <select v-model="replyTime">
                  <option
                    v-for="option in getReplyTimeOptions"
                    :key="option.key"
                    :value="option.value"
                  >
                    {{ option.text }}
                  </option>
                </select>
              </label>
            </div>

            <!-- Bubble Tab -->
            <div v-show="activeTab === 'bubble'" class="space-y-4">
              <InputRadioGroup
                name="widget-bubble-position"
                :label="
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION_LABEL'
                  )
                "
                :items="widgetBubblePositions"
                :action="handleWidgetBubblePositionChange"
              />
              <InputRadioGroup
                name="widget-bubble-type"
                :label="
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE_LABEL'
                  )
                "
                :items="widgetBubbleTypes"
                :action="handleWidgetBubbleTypeChange"
              />
              <Input
                v-model="widgetBubbleLauncherTitle"
                name="widgetBubbleLauncherTitle"
                :label="
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.LABEL'
                  )
                "
                :placeholder="
                  $t(
                    'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.PLACE_HOLDER'
                  )
                "
              />

              <!-- Widget Icon -->
              <div class="mt-4">
                <label
                  class="text-sm font-medium text-slate-800 dark:text-slate-100 block mb-2"
                >
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.BUBBLE_ICON_NAME.LABEL'
                    )
                  }}
                </label>

                <!-- Mode toggle -->
                <div
                  class="flex gap-1 p-1 bg-n-slate-3 dark:bg-n-slate-4 rounded-lg mb-4 w-fit"
                >
                  <button
                    type="button"
                    class="px-3 py-1.5 text-sm rounded-md transition-colors"
                    :class="
                      iconMode === 'none'
                        ? 'bg-white dark:bg-n-solid-3 text-n-slate-12 shadow-sm'
                        : 'text-n-slate-11 hover:text-n-slate-12'
                    "
                    @click="setIconMode('none')"
                  >
                    {{
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.BUBBLE_ICON_NAME.NONE'
                      )
                    }}
                  </button>
                  <button
                    type="button"
                    class="px-3 py-1.5 text-sm rounded-md transition-colors"
                    :class="
                      iconMode === 'predefined'
                        ? 'bg-white dark:bg-n-solid-3 text-n-slate-12 shadow-sm'
                        : 'text-n-slate-11 hover:text-n-slate-12'
                    "
                    @click="setIconMode('predefined')"
                  >
                    {{
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.BUBBLE_ICON_NAME.MODE_PREDEFINED'
                      )
                    }}
                  </button>
                  <button
                    type="button"
                    class="px-3 py-1.5 text-sm rounded-md transition-colors"
                    :class="
                      iconMode === 'custom'
                        ? 'bg-white dark:bg-n-solid-3 text-n-slate-12 shadow-sm'
                        : 'text-n-slate-11 hover:text-n-slate-12'
                    "
                    @click="setIconMode('custom')"
                  >
                    {{
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.BUBBLE_ICON_NAME.MODE_CUSTOM'
                      )
                    }}
                  </button>
                </div>

                <!-- Predefined icons -->
                <div
                  v-if="iconMode === 'predefined'"
                  class="flex flex-wrap gap-2"
                >
                  <button
                    v-for="icon in bubbleIcons"
                    :key="icon.value"
                    type="button"
                    class="flex items-center justify-center w-10 h-10 rounded-lg border-2 transition-colors"
                    :class="
                      bubbleIconName === icon.value
                        ? 'border-woot-500 bg-woot-25 dark:bg-woot-800/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    "
                    :title="icon.label"
                    @click="bubbleIconName = icon.value"
                  >
                    <span :class="icon.icon" class="text-lg" />
                  </button>
                </div>

                <!-- Custom image upload -->
                <template v-if="iconMode === 'custom'">
                  <IconUploader
                    :src="customBubbleIconUrl"
                    :size="80"
                    placeholder-icon="i-lucide-message-circle"
                    @upload="handleCustomBubbleIconUpload"
                    @delete="handleCustomBubbleIconDelete"
                  />
                  <div class="grid grid-cols-2 gap-4 mt-4">
                    <Input
                      v-model.number="customBubbleWidth"
                      name="customBubbleWidth"
                      type="number"
                      :label="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.CUSTOM_BUBBLE_WIDTH.LABEL'
                        )
                      "
                      :placeholder="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.CUSTOM_BUBBLE_WIDTH.PLACEHOLDER'
                        )
                      "
                      min="32"
                      max="200"
                    />
                    <Input
                      v-model.number="customBubbleHeight"
                      name="customBubbleHeight"
                      type="number"
                      :label="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.CUSTOM_BUBBLE_HEIGHT.LABEL'
                        )
                      "
                      :placeholder="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.CUSTOM_BUBBLE_HEIGHT.PLACEHOLDER'
                        )
                      "
                      min="32"
                      max="200"
                    />
                  </div>
                </template>
              </div>

              <div class="mt-4 border-t border-n-weak pt-4">
                <button
                  type="button"
                  class="flex items-center gap-1.5 text-sm font-medium text-n-slate-11 hover:text-n-slate-12"
                  @click="showBubbleAdvanced = !showBubbleAdvanced"
                >
                  <span
                    class="i-lucide-chevron-right size-4 transition-transform"
                    :class="{ 'rotate-90': showBubbleAdvanced }"
                  />
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.BUBBLE_ADVANCED.LABEL'
                    )
                  }}
                </button>
                <div v-if="showBubbleAdvanced" class="mt-3">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      v-model="preferBuilderSettings"
                      type="checkbox"
                      class="rounded"
                    />
                    <span class="text-sm font-medium text-n-slate-12">
                      {{
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.PREFER_BUILDER_SETTINGS.LABEL'
                        )
                      }}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Advanced Tab -->
            <div v-show="activeTab === 'advanced'" class="space-y-4">
              <!-- Allowed URL patterns -->
              <div class="space-y-2">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.ALLOWED_URL_PATTERNS.LABEL'
                    )
                  }}
                </h3>
                <p class="text-sm text-n-slate-11">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.ALLOWED_URL_PATTERNS.HELP'
                    )
                  }}
                </p>
                <div
                  v-for="(pattern, index) in allowedUrlPatterns"
                  :key="`allowed-url-pattern-${index}`"
                  class="flex items-start gap-2"
                >
                  <div class="flex-1">
                    <input
                      v-model="allowedUrlPatterns[index]"
                      type="text"
                      class="w-full rounded-lg border-0 px-3 py-2 text-sm text-n-slate-12 outline outline-1 outline-n-weak bg-n-alpha-black2 focus:outline-n-brand"
                      :class="{
                        'outline-n-ruby-8 focus:outline-n-ruby-9':
                          !!allowedUrlPatternErrors[index],
                      }"
                      :placeholder="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.ALLOWED_URL_PATTERNS.PLACEHOLDER'
                        )
                      "
                      @blur="validateAllowedUrlPatterns"
                    />
                    <p
                      v-if="allowedUrlPatternErrors[index]"
                      class="mt-1 text-xs text-n-ruby-9"
                    >
                      {{ allowedUrlPatternErrors[index] }}
                    </p>
                  </div>
                  <NextButton
                    type="button"
                    variant="ghost"
                    color="ruby"
                    size="sm"
                    icon="i-lucide-trash-2"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.ALLOWED_URL_PATTERNS.REMOVE'
                      )
                    "
                    @click="removeAllowedUrlPattern(index)"
                  />
                </div>
                <NextButton
                  type="button"
                  variant="faded"
                  size="sm"
                  icon="i-lucide-plus"
                  :label="
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.ALLOWED_URL_PATTERNS.ADD'
                    )
                  "
                  @click="addAllowedUrlPattern"
                />
              </div>
              <!-- Disallowed URL patterns -->
              <div class="space-y-2">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISALLOWED_URL_PATTERNS.LABEL'
                    )
                  }}
                </h3>
                <p class="text-sm text-n-slate-11">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISALLOWED_URL_PATTERNS.HELP'
                    )
                  }}
                </p>
                <div
                  v-for="(pattern, index) in disallowedUrlPatterns"
                  :key="`disallowed-url-pattern-${index}`"
                  class="flex items-start gap-2"
                >
                  <div class="flex-1">
                    <input
                      v-model="disallowedUrlPatterns[index]"
                      type="text"
                      class="w-full rounded-lg border-0 px-3 py-2 text-sm text-n-slate-12 outline outline-1 outline-n-weak bg-n-alpha-black2 focus:outline-n-brand"
                      :class="{
                        'outline-n-ruby-8 focus:outline-n-ruby-9':
                          !!disallowedUrlPatternErrors[index],
                      }"
                      :placeholder="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISALLOWED_URL_PATTERNS.PLACEHOLDER'
                        )
                      "
                      @blur="validateDisallowedUrlPatterns"
                    />
                    <p
                      v-if="disallowedUrlPatternErrors[index]"
                      class="mt-1 text-xs text-n-ruby-9"
                    >
                      {{ disallowedUrlPatternErrors[index] }}
                    </p>
                  </div>
                  <NextButton
                    type="button"
                    variant="ghost"
                    color="ruby"
                    size="sm"
                    icon="i-lucide-trash-2"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISALLOWED_URL_PATTERNS.REMOVE'
                      )
                    "
                    @click="removeDisallowedUrlPattern(index)"
                  />
                </div>
                <NextButton
                  type="button"
                  variant="faded"
                  size="sm"
                  icon="i-lucide-plus"
                  :label="
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.DISALLOWED_URL_PATTERNS.ADD'
                    )
                  "
                  @click="addDisallowedUrlPattern"
                />
              </div>
              <div class="space-y-2">
                <label class="flex items-start gap-2 cursor-pointer">
                  <input
                    v-model="watchUrlChanges"
                    type="checkbox"
                    class="mt-0.5 rounded"
                  />
                  <span>
                    <span class="block text-sm font-medium text-n-slate-12">
                      {{
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WATCH_URL_CHANGES.LABEL'
                        )
                      }}
                    </span>
                    <span class="mt-1 block text-sm text-n-slate-11">
                      {{
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WATCH_URL_CHANGES.HELP'
                        )
                      }}
                    </span>
                  </span>
                </label>
              </div>
              <!-- Experiences -->
              <div class="space-y-2">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ $t('WIDGET_EXPERIENCES_CONFIGURATION') }}
                </h3>
                <p class="text-sm text-n-slate-11">
                  {{ $t('WIDGET_EXPERIENCES_CONFIGURATION_DESCRIPTION') }}
                </p>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    v-model="experiencesEnabled"
                    type="checkbox"
                    class="rounded"
                  />
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ $t('WIDGET_EXPERIENCES_ENABLED_LABEL') }}
                  </span>
                </label>
                <WidgetExperiencesChecklist
                  v-if="experiencesEnabled"
                  v-model="selectedWidgetExperienceIds"
                  :experiences="planningExperiences || []"
                  :is-loading="planningExperiencesUiFlags.isFetching"
                />
              </div>
              <ConversationStarters
                :conversation-starters="conversationStarters"
                :new-starter="newStarter"
                :is-editing="isEditing"
                :can-add-starter="canAddStarter"
                @add-starter="addConversationStarter"
                @edit-starter="editConversationStarter"
                @delete-starter="deleteConversationStarter"
                @reset-starter="resetNewStarter"
              />
            </div>

            <!-- Widget Theme Tab -->
            <div v-show="activeTab === 'widget-theme'" class="space-y-6">
              <div class="flex items-start justify-between gap-4">
                <p class="text-sm text-n-slate-11">
                  {{ $t('INBOX_MGMT.WIDGET_BUILDER.THEME.HELP_TEXT') }}
                </p>
                <NextButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  :label="$t('INBOX_MGMT.WIDGET_BUILDER.THEME.RESET_THEME')"
                  @click="resetWidgetTheme"
                />
              </div>

              <!-- Widget Theme Fields -->
              <!-- Background Image -->
              <div class="space-y-3">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.THEME.SECTIONS.BACKGROUND_IMAGE'
                    )
                  }}
                </h3>
                <GalleryImagePicker v-model="widgetTheme.backgroundImageUrl" />
              </div>

              <!-- Colors -->
              <div class="space-y-4">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ $t('INBOX_MGMT.WIDGET_BUILDER.THEME.SECTIONS.COLORS') }}
                </h3>
                <div class="grid grid-cols-2 gap-4">
                  <ColorInput
                    v-model="widgetTheme.primaryColor"
                    :label="
                      $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.PRIMARY_COLOR')
                    "
                  />
                  <ColorInput
                    v-model="widgetTheme.backgroundColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BACKGROUND_COLOR'
                      )
                    "
                  />
                  <ColorInput
                    v-model="widgetTheme.bannerBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BANNER_BG_COLOR'
                      )
                    "
                  />
                  <ColorInput
                    v-model="widgetTheme.bannerTextColor"
                    :contrast-with="widgetTheme.bannerBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BANNER_TEXT_COLOR'
                      )
                    "
                  />
                  <ColorInput
                    v-model="widgetTheme.bannerIconColor"
                    :contrast-with="widgetTheme.bannerBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BANNER_ICON_COLOR'
                      )
                    "
                  />
                  <ColorInput
                    v-model="widgetTheme.userBubbleBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.USER_BUBBLE_BG'
                      )
                    "
                  />
                  <ColorInput
                    v-model="widgetTheme.userBubbleTextColor"
                    :contrast-with="widgetTheme.userBubbleBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.USER_BUBBLE_TEXT'
                      )
                    "
                  />
                  <ColorInput
                    v-model="widgetTheme.agentBubbleBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.AGENT_BUBBLE_BG'
                      )
                    "
                  />
                  <ColorInput
                    v-model="widgetTheme.agentBubbleTextColor"
                    :contrast-with="widgetTheme.agentBubbleBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.AGENT_BUBBLE_TEXT'
                      )
                    "
                  />
                  <ColorInput
                    v-model="widgetTheme.statusBubbleBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.STATUS_BUBBLE_BG'
                      )
                    "
                  />
                  <ColorInput
                    v-model="widgetTheme.statusBubbleTextColor"
                    :contrast-with="widgetTheme.statusBubbleBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.STATUS_BUBBLE_TEXT'
                      )
                    "
                  />
                  <div class="col-span-2">
                    <ColorInput
                      v-model="widgetTheme.mutedTextColor"
                      :label="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.MUTED_TEXT_COLOR'
                        )
                      "
                    />
                  </div>
                  <ColorInput
                    v-model="widgetTheme.inputBgColor"
                    :label="
                      $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.INPUT_BG')
                    "
                  />
                  <ColorInput
                    v-model="widgetTheme.inputTextColor"
                    :contrast-with="widgetTheme.inputBgColor"
                    :label="
                      $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.INPUT_TEXT')
                    "
                  />
                  <div class="col-span-2">
                    <ColorInput
                      v-model="widgetTheme.inputPlaceholderColor"
                      :label="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.INPUT_PLACEHOLDER'
                        )
                      "
                    />
                  </div>
                  <div class="col-span-2">
                    <ColorInput
                      v-model="widgetTheme.formFieldBgColor"
                      :label="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.FORM_FIELD_BG'
                        )
                      "
                    />
                  </div>
                  <div class="col-span-2">
                    <ColorInput
                      v-model="widgetTheme.borderColor"
                      :label="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BORDER_COLOR'
                        )
                      "
                    />
                  </div>
                </div>
              </div>

              <!-- Border & Shadow -->
              <div class="space-y-4">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{
                    $t('INBOX_MGMT.WIDGET_BUILDER.THEME.SECTIONS.BORDER_SHADOW')
                  }}
                </h3>
                <Input
                  v-model.number="widgetTheme.borderRadius"
                  name="borderRadius"
                  type="number"
                  :label="
                    $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BORDER_RADIUS')
                  "
                  :placeholder="
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BORDER_RADIUS_PLACEHOLDER'
                    )
                  "
                  min="0"
                  max="32"
                />
              </div>

              <!-- Typography -->
              <div class="space-y-4">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{
                    $t('INBOX_MGMT.WIDGET_BUILDER.THEME.SECTIONS.TYPOGRAPHY')
                  }}
                </h3>
                <label>
                  {{ $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.FONT_FAMILY') }}
                  <select v-model="widgetTheme.fontFamily">
                    <option value="">
                      {{ $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FONT_DEFAULT') }}
                    </option>
                    <option value="Inter">Inter</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Lato">Lato</option>
                    <option value="Open Sans">Open Sans</option>
                    <option value="Poppins">Poppins</option>
                    <option value="Nunito">Nunito</option>
                    <option value="Raleway">Raleway</option>
                    <option value="Montserrat">Montserrat</option>
                    <option value="Source Sans 3">Source Sans 3</option>
                    <option value="Playfair Display">Playfair Display</option>
                    <option value="Merriweather">Merriweather</option>
                    <option value="Ubuntu">Ubuntu</option>
                    <option value="Oswald">Oswald</option>
                    <option value="PT Sans">PT Sans</option>
                    <option value="Karla">Karla</option>
                    <option value="DM Sans">DM Sans</option>
                    <option value="Work Sans">Work Sans</option>
                    <option value="Mulish">Mulish</option>
                    <option value="Jost">Jost</option>
                    <option value="Comic Neue">Comic Neue</option>
                  </select>
                </label>
              </div>
            </div>

            <!-- Dialogue Theme Tab -->
            <div v-show="activeTab === 'dialogue-theme'" class="space-y-6">
              <div class="flex items-start justify-between gap-4">
                <p class="text-sm text-n-slate-11">
                  {{ $t('INBOX_MGMT.WIDGET_BUILDER.THEME.HELP_TEXT') }}
                </p>
                <NextButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  :label="$t('INBOX_MGMT.WIDGET_BUILDER.THEME.RESET_THEME')"
                  @click="resetDialogueTheme"
                />
              </div>

              <!-- Layout options -->
              <div class="flex flex-col gap-3">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    v-model="homeViewEnabled"
                    type="checkbox"
                    class="rounded"
                  />
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ $t('WIDGET_HOME_VIEW_ENABLED_LABEL') }}
                  </span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input v-model="showHeader" type="checkbox" class="rounded" />
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ $t('WIDGET_SHOW_HEADER_ENABLED_LABEL') }}
                  </span>
                </label>
              </div>

              <!-- Dialogue Theme Fields -->
              <!-- Background Image -->
              <div class="space-y-3">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.THEME.SECTIONS.BACKGROUND_IMAGE'
                    )
                  }}
                </h3>
                <GalleryImagePicker
                  v-model="dialogueTheme.backgroundImageUrl"
                />
              </div>

              <!-- Colors -->
              <div class="space-y-4">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ $t('INBOX_MGMT.WIDGET_BUILDER.THEME.SECTIONS.COLORS') }}
                </h3>
                <div class="grid grid-cols-2 gap-4">
                  <ColorInput
                    v-model="dialogueTheme.primaryColor"
                    :label="
                      $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.PRIMARY_COLOR')
                    "
                  />
                  <ColorInput
                    v-model="dialogueTheme.backgroundColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BACKGROUND_COLOR'
                      )
                    "
                  />
                  <ColorInput
                    v-model="dialogueTheme.bannerBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BANNER_BG_COLOR'
                      )
                    "
                  />
                  <ColorInput
                    v-model="dialogueTheme.bannerTextColor"
                    :contrast-with="dialogueTheme.bannerBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BANNER_TEXT_COLOR'
                      )
                    "
                  />
                  <ColorInput
                    v-model="dialogueTheme.bannerIconColor"
                    :contrast-with="dialogueTheme.bannerBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BANNER_ICON_COLOR'
                      )
                    "
                  />
                  <ColorInput
                    v-model="dialogueTheme.userBubbleBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.USER_BUBBLE_BG'
                      )
                    "
                  />
                  <ColorInput
                    v-model="dialogueTheme.userBubbleTextColor"
                    :contrast-with="dialogueTheme.userBubbleBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.USER_BUBBLE_TEXT'
                      )
                    "
                  />
                  <ColorInput
                    v-model="dialogueTheme.agentBubbleTextColor"
                    class="col-span-2"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.AGENT_BUBBLE_TEXT'
                      )
                    "
                  />
                  <ColorInput
                    v-model="dialogueTheme.statusBubbleBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.STATUS_BUBBLE_BG'
                      )
                    "
                  />
                  <ColorInput
                    v-model="dialogueTheme.statusBubbleTextColor"
                    :contrast-with="dialogueTheme.statusBubbleBgColor"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.STATUS_BUBBLE_TEXT'
                      )
                    "
                  />
                  <div class="col-span-2">
                    <ColorInput
                      v-model="dialogueTheme.mutedTextColor"
                      :label="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.MUTED_TEXT_COLOR'
                        )
                      "
                    />
                  </div>
                  <ColorInput
                    v-model="dialogueTheme.inputBgColor"
                    :label="
                      $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.INPUT_BG')
                    "
                  />
                  <ColorInput
                    v-model="dialogueTheme.inputTextColor"
                    :contrast-with="dialogueTheme.inputBgColor"
                    :label="
                      $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.INPUT_TEXT')
                    "
                  />
                  <div class="col-span-2">
                    <ColorInput
                      v-model="dialogueTheme.inputPlaceholderColor"
                      :label="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.INPUT_PLACEHOLDER'
                        )
                      "
                    />
                  </div>
                  <div class="col-span-2">
                    <ColorInput
                      v-model="dialogueTheme.formFieldBgColor"
                      :label="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.FORM_FIELD_BG'
                        )
                      "
                    />
                  </div>
                  <div class="col-span-2">
                    <ColorInput
                      v-model="dialogueTheme.borderColor"
                      :label="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BORDER_COLOR'
                        )
                      "
                    />
                  </div>
                </div>
              </div>

              <!-- Border -->
              <div class="space-y-4">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{
                    $t('INBOX_MGMT.WIDGET_BUILDER.THEME.SECTIONS.BORDER_SHADOW')
                  }}
                </h3>
                <Input
                  v-model.number="dialogueTheme.borderRadius"
                  name="dialogueBorderRadius"
                  type="number"
                  :label="
                    $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BORDER_RADIUS')
                  "
                  :placeholder="
                    $t(
                      'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BORDER_RADIUS_PLACEHOLDER'
                    )
                  "
                  min="0"
                  max="32"
                />
                <label>
                  {{ $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BOX_SHADOW') }}
                  <select v-model="dialogueTheme.boxShadow">
                    <option value="">
                      {{
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.SHADOW_PRESETS.DEFAULT'
                        )
                      }}
                    </option>
                    <option value="none">
                      {{
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.SHADOW_PRESETS.NONE'
                        )
                      }}
                    </option>
                    <option value="0 2px 8px rgba(0, 0, 0, 0.08)">
                      {{
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.SHADOW_PRESETS.SUBTLE'
                        )
                      }}
                    </option>
                    <option value="0 8px 40px rgba(0, 0, 0, 0.16)">
                      {{
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.SHADOW_PRESETS.STANDARD'
                        )
                      }}
                    </option>
                    <option value="0 20px 60px rgba(0, 0, 0, 0.30)">
                      {{
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.THEME.SHADOW_PRESETS.DRAMATIC'
                        )
                      }}
                    </option>
                  </select>
                </label>
              </div>

              <!-- Typography -->
              <div class="space-y-4">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{
                    $t('INBOX_MGMT.WIDGET_BUILDER.THEME.SECTIONS.TYPOGRAPHY')
                  }}
                </h3>
                <label>
                  {{ $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.FONT_FAMILY') }}
                  <select v-model="dialogueTheme.fontFamily">
                    <option value="">
                      {{ $t('INBOX_MGMT.WIDGET_BUILDER.THEME.FONT_DEFAULT') }}
                    </option>
                    <option value="Inter">Inter</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Lato">Lato</option>
                    <option value="Open Sans">Open Sans</option>
                    <option value="Poppins">Poppins</option>
                    <option value="Nunito">Nunito</option>
                    <option value="Raleway">Raleway</option>
                    <option value="Montserrat">Montserrat</option>
                    <option value="Source Sans 3">Source Sans 3</option>
                    <option value="Playfair Display">Playfair Display</option>
                    <option value="Merriweather">Merriweather</option>
                    <option value="Ubuntu">Ubuntu</option>
                    <option value="Oswald">Oswald</option>
                    <option value="PT Sans">PT Sans</option>
                    <option value="Karla">Karla</option>
                    <option value="DM Sans">DM Sans</option>
                    <option value="Work Sans">Work Sans</option>
                    <option value="Mulish">Mulish</option>
                    <option value="Jost">Jost</option>
                    <option value="Comic Neue">Comic Neue</option>
                  </select>
                </label>
              </div>
            </div>

            <NextButton
              type="submit"
              class="mt-4"
              :label="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.BUTTON_TEXT'
                )
              "
              :is-loading="uiFlags.isUpdating"
              :disabled="v$.$invalid || uiFlags.isUpdating"
            />
          </form>
        </div>
      </div>
      <div class="flex-shrink-0 w-[320px] py-4">
        <!-- Widget theme preview -->
        <template v-if="activeTab !== 'dialogue-theme'">
          <Tabs
            :index="isWidgetPreview ? 0 : 1"
            class="px-0 mb-6"
            @change="isWidgetPreview = $event === 0"
          >
            <TabsItem
              :index="0"
              :name="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_VIEW_OPTION.PREVIEW'
                )
              "
              :show-badge="false"
            />
            <TabsItem
              :index="1"
              :name="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_VIEW_OPTION.SCRIPT'
                )
              "
              :show-badge="false"
            />
          </Tabs>
          <div
            v-if="isWidgetPreview"
            class="flex flex-col items-center justify-end min-h-[40.625rem] mx-5 mb-5 p-2.5 border-slate-900/50 dark:bg-slate-900/50 rounded-lg"
          >
            <Widget
              :welcome-heading="welcomeHeading"
              :welcome-tagline="welcomeTagline"
              :website-name="websiteName"
              :logo="avatarUrl"
              is-online
              :reply-time="replyTime"
              :color="color"
              :widget-bubble-position="widgetBubblePosition"
              :widget-bubble-launcher-title="widgetBubbleLauncherTitle"
              :widget-bubble-type="widgetBubbleType"
              :custom-bubble-icon="customBubbleIconUrl"
              :custom-bubble-width="customBubbleWidth"
              :custom-bubble-height="customBubbleHeight"
              :widget-theme="widgetTheme"
              :bubble-icon-name="bubbleIconName"
            />
          </div>
          <div
            v-else
            class="mx-5 p-2.5 bg-n-slate-3 rounded-lg dark:bg-n-solid-3"
          >
            <woot-code :script="widgetScript" />
          </div>
        </template>

        <!-- Dialogue theme preview -->
        <template v-if="activeTab === 'dialogue-theme'">
          <Tabs
            :index="isDialoguePreview ? 0 : 1"
            class="px-0 mb-6"
            @change="isDialoguePreview = $event === 0"
          >
            <TabsItem
              :index="0"
              :name="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_VIEW_OPTION.PREVIEW'
                )
              "
              :show-badge="false"
            />
            <TabsItem
              :index="1"
              :name="
                $t(
                  'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_VIEW_OPTION.SCRIPT'
                )
              "
              :show-badge="false"
            />
          </Tabs>
          <div
            v-if="isDialoguePreview"
            class="flex flex-col items-center min-h-[40.625rem] mx-5 mb-5 p-2.5"
          >
            <!-- Home / Conversation sub-tabs -->
            <Tabs
              v-if="homeViewEnabled"
              :index="dialoguePreviewView === 'home' ? 0 : 1"
              class="w-full mb-4"
              :border="false"
              @change="
                dialoguePreviewView = $event === 0 ? 'home' : 'conversation'
              "
            >
              <TabsItem :index="0" name="Home" :show-badge="false" is-compact />
              <TabsItem
                :index="1"
                name="Conversation"
                :show-badge="false"
                is-compact
              />
            </Tabs>
            <DialoguePreview
              :config="{
                welcomeHeading,
                welcomeTagline,
                websiteName,
                logo: avatarUrl,
                showHeader,
              }"
              :view="homeViewEnabled ? dialoguePreviewView : 'conversation'"
              :style="previewThemeVars"
            />
          </div>
          <div
            v-else
            class="mx-5 p-2.5 bg-n-slate-3 rounded-lg dark:bg-n-solid-3"
          >
            <woot-code :script="dialogueScript" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
